<script lang="ts">
  import { t } from '$core/i18n';
  import {
    identity,
    signIn,
    signUp,
    signOut,
    resetPassword,
    type AuthCredentials
  } from '$core/identity';
  import { isSupabaseConfigured } from '$db/remote/supabase';
  import {
    exportForeignQueue,
    getQueueSummaryForUser,
    listForeignQueues,
    downloadJson,
    type ForeignQueueSummary
  } from '$features/sync';
  import PendingQueueModal from '$ui/components/PendingQueueModal.svelte';
  import ForeignQueuesModal from '$ui/components/ForeignQueuesModal.svelte';
  import { Modal } from '$ui/primitives/Modal';
  import { Button } from '$ui/primitives/Button';
  import { Input } from '$ui/primitives/Input';

  let { onClose }: { onClose: () => void } = $props();

  type Mode = 'signin' | 'signup' | 'forgot';

  let mode = $state<Mode>('signin');
  let email = $state('');
  let password = $state('');
  let busy = $state(false);
  let errorMessage = $state<string | null>(null);
  let resetSent = $state(false);
  let pendingQueue = $state<{
    outboxCount: number;
    outboxBytes: number;
    quarantineCount: number;
    quarantineBytes: number;
  } | null>(null);
  let foreignQueues = $state<ForeignQueueSummary[] | null>(null);
  let hasForeignQueues = $state(false);

  const configured = isSupabaseConfigured();

  let canSubmit = $derived(
    configured && email.trim().length > 0 && (mode === 'forgot' || password.length >= 6) && !busy
  );

  async function submit() {
    if (!canSubmit) return;
    busy = true;
    errorMessage = null;
    resetSent = false;

    try {
      if (mode === 'forgot') {
        await resetPassword(email.trim());
        resetSent = true;
        busy = false;
        return;
      }

      const creds: AuthCredentials = { email: email.trim(), password };
      if (mode === 'signin') {
        await signIn(creds);
      } else {
        await signUp(creds);
      }
      // After successful auth, check for orphaned ops from other accounts.
      const foreign = await listForeignQueues(identity.userId);
      if (foreign.length > 0) {
        foreignQueues = foreign;
        return;
      }
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      // TEMPORAL PARA DEBUG: Mostrar el error crudo de Supabase
      errorMessage = `DEBUG: ${message}`; 
      // errorMessage = mapError(message);
    } finally {
      busy = false;
    }
  }

  async function refreshForeignFlag(): Promise<void> {
    if (!identity.isAuthenticated) {
      hasForeignQueues = false;
      return;
    }
    const foreign = await listForeignQueues(identity.userId);
    hasForeignQueues = foreign.length > 0;
  }

  function openForeignQueues(): void {
    if (!identity.isAuthenticated) return;
    void listForeignQueues(identity.userId).then((list) => {
      foreignQueues = list;
    });
  }

  function closeForeignQueues(): void {
    foreignQueues = null;
    void refreshForeignFlag();
  }

  function onForeignChanged(): void {
    if (!identity.isAuthenticated) return;
    void listForeignQueues(identity.userId).then((list) => {
      if (list.length === 0) {
        foreignQueues = null;
        hasForeignQueues = false;
      } else {
        foreignQueues = list;
        hasForeignQueues = true;
      }
    });
  }

  $effect(() => {
    if (identity.isAuthenticated && identity.identity.userId) {
      void refreshForeignFlag();
    }
  });



  async function handleSignOut() {
    busy = true;
    try {
      const summary = await getQueueSummaryForUser(identity.userId);
      if (summary.outboxCount + summary.quarantineCount > 0) {
        pendingQueue = {
          outboxCount: summary.outboxCount,
          outboxBytes: summary.outboxBytes,
          quarantineCount: summary.quarantineCount,
          quarantineBytes: summary.quarantineBytes
        };
        return;
      }
      await safeSignOut();
    } catch (err) {
      console.warn('[StudyQuantifier] signOut precheck failed:', err);
    } finally {
      busy = false;
    }
  }

  /**
   * Tolerante a fallos de red: en Supabase JS v2, signOut() limpia la
   * sesión local ANTES de intentar avisar al servidor. Si la red falla,
   * la sesión local ya está cerrada — no queremos que ese error bloquee
   * la limpieza de la UI ni deje modales colgados.
   */
  async function safeSignOut(): Promise<void> {
    try {
      await signOut();
    } catch (err) {
      console.warn(
        '[StudyQuantifier] signOut network error (local session already cleared):',
        err
      );
    }
  }

  async function doSignOut(): Promise<void> {
    busy = true;
    try {
      await safeSignOut();
    } finally {
      pendingQueue = null;
      busy = false;
    }
  }

  async function exportAndSignOut(): Promise<void> {
    if (!pendingQueue) return;
    busy = true;
    try {
      const data = await exportForeignQueue(identity.userId);
      const shortId = identity.userId.slice(0, 8);
      const date = new Date().toISOString().slice(0, 10);
      downloadJson(`studyquantifier-queue-${shortId}-${date}.json`, data);
    } catch (err) {
      console.error('[StudyQuantifier] export failed, aborting signOut:', err);
      busy = false;
      return;
    }
    try {
      await safeSignOut();
    } finally {
      pendingQueue = null;
      busy = false;
    }
  }

  async function leaveAndSignOut(): Promise<void> {
    if (!pendingQueue) return;
    await doSignOut();
  }

  function cancelPendingSignOut(): void {
    pendingQueue = null;
  }

  let open = $state(true);
</script>

<Modal
  bind:open
  title={t('auth.title')}
  size="sm"
  onclose={onClose}
>
  <div class="body">
    {#if !configured}
      <p class="error">Supabase not configured.</p>
    {:else if identity.isAuthenticated}
      <div class="signed-in">
        <span class="label">{t('auth.signedInAs')}</span>
        <span class="value">{identity.email ?? identity.userId}</span>
      </div>
      <div class="row-actions end">
        <Button variant="danger" disabled={busy} loading={busy} onclick={() => void handleSignOut()}>
          {busy ? t('auth.busy') : t('auth.signOut')}
        </Button>
      </div>
      {#if hasForeignQueues}
        <Button variant="link" onclick={openForeignQueues}>
          {t('foreignQueues.title')}
        </Button>
      {/if}
    {:else}
      <p class="hint">{t('auth.guestBanner')}</p>

      {#if resetSent}
        <div class="success" role="status" style="color: var(--color-success); font-size: var(--text-sm); margin-bottom: var(--space-4); padding: var(--space-3); background: var(--color-success-tint); border-radius: var(--radius-sm);">
          Password reset link sent! Check your email inbox.
        </div>
        <div class="row-actions end">
          <Button variant="outline" onclick={() => { mode = 'signin'; resetSent = false; }}>
            Back to Sign In
          </Button>
        </div>
      {:else}
        <Input
          type="email"
          label={t('auth.email')}
          autocomplete="email"
          bind:value={email}
          onkeydown={(e) => e.key === 'Enter' && void submit()}
        />

        {#if mode !== 'forgot'}
          <Input
            type="password"
            label={t('auth.password')}
            autocomplete={mode === 'signin' ? 'current-password' : 'new-password'}
            helper={t('auth.passwordHint')}
            bind:value={password}
            onkeydown={(e) => e.key === 'Enter' && void submit()}
          />
        {/if}

        {#if errorMessage}
          <div class="error" role="alert">{errorMessage}</div>
        {/if}

        <div class="row-actions end">
          <Button variant="filled" disabled={!canSubmit} loading={busy} onclick={() => void submit()}>
            {busy
              ? t('auth.busy')
              : mode === 'forgot'
                ? 'Send Reset Link'
                : mode === 'signin'
                  ? t('auth.submitSignIn')
                  : t('auth.submitSignUp')}
          </Button>
        </div>

        <div class="toggle-link" style="display: flex; justify-content: space-between; margin-top: var(--space-3);">
          {#if mode === 'signin'}
            <Button variant="link" onclick={() => mode = 'forgot'}>
              Forgot password?
            </Button>
            <Button variant="link" onclick={() => mode = 'signup'}>
              {t('auth.switchToSignUp')}
            </Button>
          {:else if mode === 'forgot'}
            <Button variant="link" onclick={() => mode = 'signin'}>
              Back to Sign In
            </Button>
          {:else}
            <Button variant="link" onclick={() => mode = 'signin'}>
              {t('auth.switchToSignIn')}
            </Button>
          {/if}
        </div>
      {/if}
    {/if}
  </div>
</Modal>

{#if pendingQueue}
  <PendingQueueModal
    outboxCount={pendingQueue.outboxCount}
    outboxBytes={pendingQueue.outboxBytes}
    quarantineCount={pendingQueue.quarantineCount}
    quarantineBytes={pendingQueue.quarantineBytes}
    {busy}
    onExport={() => void exportAndSignOut()}
    onLeave={() => void leaveAndSignOut()}
    onCancel={cancelPendingSignOut}
  />
{/if}

{#if foreignQueues && identity.isAuthenticated}
  <ForeignQueuesModal
    currentUserId={identity.userId}
    queues={foreignQueues}
    onClose={closeForeignQueues}
    onChanged={onForeignChanged}
  />
{/if}

<style>
  .body {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }

  .hint {
    margin: 0;
    font-size: var(--text-sm);
    color: var(--color-text-muted);
  }

  .signed-in {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
    padding: var(--space-3);
    background: var(--color-surface-sunken);
    border-radius: var(--radius-md);
  }

  .signed-in .label {
    font-size: var(--text-label);
    font-weight: var(--text-label-weight);
    letter-spacing: var(--text-label-track);
    text-transform: uppercase;
    color: var(--color-text-muted);
  }

  .signed-in .value {
    font-size: var(--text-sm);
    color: var(--color-text-primary);
    overflow-wrap: anywhere;
  }

  .error {
    padding: var(--space-3);
    font-size: var(--text-sm);
    color: var(--color-danger);
    background: var(--color-danger-tint);
    border-left: 3px solid var(--color-danger);
    border-radius: var(--radius-md);
  }

  .row-actions {
    display: flex;
    gap: var(--space-2);
  }

  .row-actions.end {
    justify-content: flex-end;
  }

  .toggle-link {
    display: flex;
    justify-content: center;
  }
</style>